import { type JSX, type ReactNode, useState } from 'react';
import { Popover } from './popover';

/**
 * The filters a table's toolbar is made of (see `Popover`): a set of values
 * out of a list, one choice out of a few views, and a range of days.
 *
 * ONE TRIGGER GRAMMAR FOR ALL THREE (founder, 2026-09-14). The trigger reads
 * the filter's NAME - "Status", "Bakery" - whatever it holds, and a count
 * beside the name says how many values are narrowing the table. It used to
 * read the chosen value itself, which worked for one value and could not say
 * "three of them" at all; and a row of triggers that each print a different
 * word depending on the state is a row whose width jumps under the reader's
 * hand. The name stays put, the count says whether it is filtering.
 *
 * AN OPTION NO ROW CARRIES IS NOT OFFERED (same day). A status that no order
 * in the table is in, a bakery that holds none of its orders, is a choice that
 * can only ever empty the table. Each option therefore carries the number of
 * rows it would match (`count`), and a zero hides it - except while it is
 * ticked, because a ticked value the reader cannot see is a filter they cannot
 * undo. Screens whose rows are all in the browser count them with
 * `optionsWithCounts`; screens whose list is paged on the server take the
 * counts from the endpoint, because the page held is not the table.
 *
 * Every string arrives already translated (PRD NFR-006).
 */

export interface FilterMenuOption {
  readonly value: string;
  readonly label: string;
  /**
   * How many rows of the table carry this value. Zero hides the option unless
   * it is chosen; omitted, the option is always offered (a list that has no way
   * to count, such as a fixed set of views).
   */
  readonly count?: number;
}

/** The words the multi-select panel prints. Already translated. */
export interface FilterMenuLabels {
  /** The search field's accessible name and placeholder. */
  readonly search: string;
  readonly selectAll: string;
  readonly clearAll: string;
}

export interface FilterMenuProps {
  /** The column's noun: what the trigger reads, always. */
  readonly label: string;
  /** The chosen values; empty means every row. */
  readonly values: readonly string[];
  readonly options: readonly FilterMenuOption[];
  /** The new set, in the order the options are listed. */
  readonly onChange: (values: readonly string[]) => void;
  readonly labels: FilterMenuLabels;
}

/** Below this many entries a list is scanned faster than it is searched. */
const SEARCH_FROM = 8;

/** The options a panel lists: counted ones that match a row, and anything chosen. */
export function offeredOptions(
  options: readonly FilterMenuOption[],
  chosen: readonly string[],
): readonly FilterMenuOption[] {
  return options.filter(
    (option) => option.count === undefined || option.count > 0 || chosen.includes(option.value),
  );
}

/** The trigger's face: the name, and the count when anything narrows. */
function TriggerFace({ label, count }: { label: string; count: number }): ReactNode {
  return (
    <>
      <span className="cib-popover__name">{label}</span>
      {count > 0 ? (
        <span className="cib-popover__count" aria-hidden="true">
          {count}
        </span>
      ) : null}
    </>
  );
}

/** The accessible name: the noun, and how many values it holds when it holds any. */
function triggerName(label: string, count: number): string {
  return count > 0 ? `${label} (${count})` : label;
}

export function FilterMenu({
  label,
  values,
  options,
  onChange,
  labels,
}: FilterMenuProps): JSX.Element {
  const offered = offeredOptions(options, values);
  return (
    <Popover
      label={triggerName(label, values.length)}
      trigger={<TriggerFace label={label} count={values.length} />}
      active={values.length > 0}
    >
      {() => (
        <CheckList
          label={label}
          values={values}
          options={offered}
          labels={labels}
          searchable={offered.length >= SEARCH_FROM}
          onChange={onChange}
        />
      )}
    </Popover>
  );
}

function CheckList({
  label,
  values,
  options,
  labels,
  searchable,
  onChange,
}: {
  readonly label: string;
  readonly values: readonly string[];
  readonly options: readonly FilterMenuOption[];
  readonly labels: FilterMenuLabels;
  readonly searchable: boolean;
  readonly onChange: (values: readonly string[]) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLocaleLowerCase();
  const entries = options.filter(
    (option) => needle === '' || option.label.toLocaleLowerCase().includes(needle),
  );
  /* Emitted in the order the options are listed, so the same set is the same
     query string however it was ticked. */
  const ordered = (next: ReadonlySet<string>): readonly string[] =>
    options.map((option) => option.value).filter((value) => next.has(value));
  const toggle = (value: string, on: boolean): void => {
    const next = new Set(values);
    if (on) next.add(value);
    else next.delete(value);
    onChange(ordered(next));
  };
  return (
    <>
      {searchable ? (
        <input
          type="search"
          className="cib-popover__search"
          aria-label={labels.search}
          placeholder={labels.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-autofocus
        />
      ) : null}
      <fieldset className="cib-popover__list cib-popover__list--checks">
        <legend className="cib-visually-hidden">{label}</legend>
        {entries.map((option) => (
          <label key={option.value} className="cib-popover__check">
            <input
              type="checkbox"
              checked={values.includes(option.value)}
              onChange={(event) => toggle(option.value, event.target.checked)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>
      <div className="cib-popover__footer">
        {/* Select all takes what the search is showing, so "type 'boul', select
            all" means every bakery matching it rather than every bakery. */}
        <button
          type="button"
          className="cib-popover__footer-action cib-popover__footer-action--primary"
          onClick={() => onChange(ordered(new Set([...values, ...entries.map((e) => e.value)])))}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M3 8.5 L6.5 12 L13 4.5" />
          </svg>
          {labels.selectAll}
        </button>
        <button
          type="button"
          className="cib-popover__footer-action"
          disabled={values.length === 0}
          onClick={() => onChange([])}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4 4 L12 12 M12 4 L4 12" />
          </svg>
          {labels.clearAll}
        </button>
      </div>
    </>
  );
}

export interface ChoiceMenuProps {
  /** The noun the trigger reads, always. */
  readonly label: string;
  /** The chosen value. */
  readonly value: string;
  readonly options: readonly FilterMenuOption[];
  readonly onChange: (value: string) => void;
  /**
   * The value that narrows nothing - the table's default view. While it is
   * chosen the trigger carries no count; any other choice reads as one.
   */
  readonly neutral: string;
}

/**
 * One choice out of a few VIEWS that nest - "in flight", "needs action",
 * "everything" - where holding two at once means nothing. Same trigger as
 * `FilterMenu`, so a toolbar reads as one row of names; the panel is a plain
 * list that closes on a choice.
 */
export function ChoiceMenu({
  label,
  value,
  options,
  onChange,
  neutral,
}: ChoiceMenuProps): JSX.Element {
  const count = value === neutral ? 0 : 1;
  return (
    <Popover
      label={triggerName(label, count)}
      trigger={<TriggerFace label={label} count={count} />}
      active={count > 0}
    >
      {(close) => (
        <div role="listbox" aria-label={label} className="cib-popover__list">
          {offeredOptions(options, [value]).map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className="cib-popover__option"
              onClick={() => {
                onChange(option.value);
                close();
              }}
              {...(option.value === value ? { 'data-autofocus': '' } : {})}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export interface DateRangeMenuProps {
  /** The noun - "Periode" - the trigger is named by. */
  readonly label: string;
  /** Both bounds as service days, YYYY-MM-DD. */
  readonly range: DateRange;
  readonly onChange: (range: DateRange) => void;
  readonly fromLabel: string;
  readonly toLabel: string;
  /** How a bound reads on the trigger - "12/08", "Sam. 12/08", as the surface prefers. */
  readonly format: (day: string) => string;
}

/**
 * Both ends of a range behind ONE trigger that reads the range itself.
 *
 * Two date boxes on the toolbar were the two widest controls on it and said
 * "Du" and "Au" where the reader wanted the dates; here the trigger prints
 * "12/08 - 03/09" and the boxes appear only while somebody is changing them.
 * A bound applies the moment it is valid - there is no Apply, because a
 * partner scanning a month changes one end and reads the rows, then the other.
 * The one trigger that prints its value rather than its name: a range is
 * always set, so a count would always read 1 and say nothing.
 */
export function DateRangeMenu({
  label,
  range,
  onChange,
  fromLabel,
  toLabel,
  format,
}: DateRangeMenuProps): JSX.Element {
  return (
    <Popover label={label} trigger={`${format(range.from)} \u2013 ${format(range.to)}`}>
      {() => (
        <div className="cib-popover__range">
          <label>
            <span>{fromLabel}</span>
            <input
              type="date"
              value={range.from}
              max={range.to}
              data-autofocus
              onChange={(event) => {
                if (event.target.value !== '') onChange({ ...range, from: event.target.value });
              }}
            />
          </label>
          <label>
            <span>{toLabel}</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(event) => {
                if (event.target.value !== '') onChange({ ...range, to: event.target.value });
              }}
            />
          </label>
        </div>
      )}
    </Popover>
  );
}
